/**
 * MoM Report — aggregated view: by person, by status, overdue, recent meetings.
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../../lib/api';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  ArrowLeft, BarChart3, AlertTriangle, CheckCircle2, Clock, Calendar,
  User, Download, TrendingUp, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(s + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const statusColor = {
  open: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-sky-50 text-sky-700',
  done: 'bg-emerald-50 text-emerald-700',
  blocked: 'bg-rose-50 text-rose-700',
};

export default function MomReportPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const r = await api.get(`/mom/report?${params}`);
      setData(r.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [['Person', 'Total', 'Open', 'In Progress', 'Done', 'Blocked', 'Overdue']];
    (data.by_person || []).forEach(p => {
      rows.push([p.person, p.total, p.open || 0, p.in_progress || 0, p.done || 0, p.blocked || 0, p.overdue || 0]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `mom_report_by_person_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const s = data?.summary || {};
  const byPerson = data?.by_person || [];
  const overdue = data?.overdue || [];
  const recent = data?.recent_meetings || [];
  const byMonth = data?.by_month || [];
  const maxMonth = Math.max(1, ...byMonth.map(m => m.count));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pb-16">
      <div className="max-w-[1400px] mx-auto px-6 pt-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav('/training/mom')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to MoM
          </Button>
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-[150px] text-sm" />
            <span className="text-slate-400 text-xs">to</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-[150px] text-sm" />
            <Button size="sm" variant="outline" onClick={exportCsv} data-testid="mom-report-export">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">MoM Report</h1>
            <p className="text-xs text-slate-500">Accountability & completion across all meetings</p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3"><Skeleton className="h-28 w-full" /><Skeleton className="h-48 w-full" /></div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
              <KpiCard label="Meetings" value={s.total_meetings} icon={Calendar} color="indigo" />
              <KpiCard label="Decisions" value={s.total_decisions} icon={FileText} color="blue" />
              <KpiCard label="Open/In-Prog" value={s.open} icon={Clock} color="amber" />
              <KpiCard label="Completed" value={s.done} icon={CheckCircle2} color="emerald" />
              <KpiCard label="Blocked" value={s.blocked} icon={AlertTriangle} color="violet" />
              <KpiCard label="Overdue" value={s.overdue} icon={AlertTriangle} color="rose" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* By Person */}
              <Card className="lg:col-span-2 p-5 border-slate-200 shadow-sm bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <User className="w-4 h-4 text-indigo-600" /> Accountability by Person
                  </h2>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{byPerson.length} people</span>
                </div>
                {byPerson.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">No decisions logged yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                          <th className="text-left py-2 font-semibold">Person</th>
                          <th className="text-right py-2 font-semibold">Total</th>
                          <th className="text-right py-2 font-semibold text-slate-600">Open</th>
                          <th className="text-right py-2 font-semibold text-sky-700">In-Prog</th>
                          <th className="text-right py-2 font-semibold text-emerald-700">Done</th>
                          <th className="text-right py-2 font-semibold text-rose-700">Blocked</th>
                          <th className="text-right py-2 font-semibold text-rose-700">Overdue</th>
                          <th className="text-left py-2 font-semibold pl-3">Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byPerson.map(p => {
                          const completionPct = p.total ? Math.round((p.done || 0) / p.total * 100) : 0;
                          return (
                            <tr key={p.person} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2.5 font-medium text-slate-800">{p.person}</td>
                              <td className="py-2.5 text-right font-bold tabular-nums">{p.total}</td>
                              <td className="py-2.5 text-right tabular-nums text-slate-600">{p.open || 0}</td>
                              <td className="py-2.5 text-right tabular-nums text-sky-700">{p.in_progress || 0}</td>
                              <td className="py-2.5 text-right tabular-nums text-emerald-700">{p.done || 0}</td>
                              <td className="py-2.5 text-right tabular-nums text-rose-700">{p.blocked || 0}</td>
                              <td className="py-2.5 text-right tabular-nums font-semibold">
                                {p.overdue > 0 ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0 text-xs">{p.overdue}</Badge> : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="py-2.5 pl-3 min-w-[120px]">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all" style={{ width: `${completionPct}%` }} />
                                  </div>
                                  <span className="text-[11px] text-slate-600 tabular-nums w-8">{completionPct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* By Month sparkline */}
              <Card className="p-5 border-slate-200 shadow-sm bg-white">
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-indigo-600" /> Meetings per Month
                </h2>
                {byMonth.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">No data</p>
                ) : (
                  <div className="space-y-2">
                    {byMonth.slice(-8).map(m => (
                      <div key={m.month} className="flex items-center gap-2 text-xs">
                        <span className="w-14 text-slate-600 font-medium tabular-nums">{m.month}</span>
                        <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-indigo-400 to-violet-500 flex items-center justify-end pr-1.5" style={{ width: `${(m.count / maxMonth) * 100}%` }}>
                            <span className="text-[10px] text-white font-semibold">{m.count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* Overdue items */}
            <Card className="p-5 mt-5 border-slate-200 shadow-sm bg-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-600" /> Overdue Action Items
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0 text-xs">{overdue.length}</Badge>
                </h2>
              </div>
              {overdue.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No overdue items. Great!</p>
              ) : (
                <div className="space-y-2">
                  {overdue.map(o => (
                    <Link key={o.decision_id} to={`/training/mom/${o.meeting_id}`} className="block">
                      <div className="flex items-start gap-3 p-3 bg-rose-50/40 border border-rose-100 rounded-lg hover:bg-rose-50 transition-colors">
                        <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0 text-[10px] mt-0.5 shrink-0">Due {fmtDate(o.due_date)}</Badge>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900 font-medium line-clamp-1">{o.decision}</p>
                          <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-600 flex-wrap">
                            <span>From: <strong>{o.meeting_title}</strong> · {fmtDate(o.meeting_date)}</span>
                            <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                              <User className="w-2.5 h-2.5 mr-1" />{o.responsible_person}
                            </Badge>
                            <Badge variant="outline" className={`text-[10px] ${statusColor[o.status] || ''}`}>{o.status}</Badge>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            {/* Recent meetings */}
            <Card className="p-5 mt-5 border-slate-200 shadow-sm bg-white">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2 mb-4">
                <Calendar className="w-4 h-4 text-indigo-600" /> Recent Meetings
              </h2>
              {recent.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No meetings in range.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {recent.map(m => (
                    <Link key={m.id} to={`/training/mom/${m.id}`} className="block">
                      <div className="p-3 border border-slate-200 rounded-lg hover:border-indigo-300 hover:shadow-sm transition-all">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{m.title}</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">{fmtDate(m.meeting_date)} · {m.meeting_type}{m.chairperson ? ` · ${m.chairperson}` : ''}</p>
                          </div>
                          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-700 border-slate-200 shrink-0 ml-2">
                            {m.decision_count} items
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }) {
  const colors = {
    indigo: 'from-indigo-50 to-violet-50 text-indigo-700 ring-indigo-100',
    blue: 'from-sky-50 to-blue-50 text-sky-700 ring-sky-100',
    amber: 'from-amber-50 to-orange-50 text-amber-700 ring-amber-100',
    emerald: 'from-emerald-50 to-green-50 text-emerald-700 ring-emerald-100',
    violet: 'from-violet-50 to-purple-50 text-violet-700 ring-violet-100',
    rose: 'from-rose-50 to-pink-50 text-rose-700 ring-rose-100',
  };
  return (
    <Card className={`p-3.5 bg-gradient-to-br ${colors[color]} ring-1 border-0 shadow-sm`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
          <p className="text-xl font-bold mt-0.5">{value ?? '—'}</p>
        </div>
        <Icon className="w-7 h-7 opacity-40" />
      </div>
    </Card>
  );
}
